// middleware/acl.global.ts
import { menuData, type MenuItem } from '@/config/menu'
import { usePermissions } from '@/composables/usePermissions'

function isItemAllowed(item: MenuItem, perms: ReturnType<typeof usePermissions>): boolean {
    const { hasPermission, hasAnyRole, hasAnyGroup, isSuperAdmin } = perms

    // Cas particulier
    if (item.has_restriction_for_admin && isSuperAdmin.value) {
        return false
    }

    // Le super admin passe partout (sauf restriction explicite ci-dessus)
    if (isSuperAdmin.value) return true

    const conds: boolean[] = []

    if (item.requirePermissionsAny?.length) {
        conds.push(item.requirePermissionsAny.some(a => hasPermission(a)))
    }
    if (item.requireRolesAny?.length) {
        conds.push(hasAnyRole(item.requireRolesAny))
    }
    if (item.requireGroupsAny?.length) {
        conds.push(hasAnyGroup(item.requireGroupsAny))
    }
    if (item.isVisible) {
        conds.push(item.isVisible({
            hasPermission,
            hasAnyRole,
            hasAnyGroup,
            isSuperAdmin: isSuperAdmin.value,
        }))
    }

    // Si aucune condition, on considère accessible
    return conds.length === 0 ? true : conds.some(Boolean)
}

function flattenMenu(items: MenuItem[]): MenuItem[] {
    const flat: MenuItem[] = []
    for (const item of items) {
        if (item.to) {
            flat.push(item)
        }
        if (item.children?.length) {
            flat.push(...flattenMenu(item.children))
        }
    }
    return flat
}

const flatMenu = flattenMenu(menuData)

export default defineNuxtRouteMiddleware((to) => {
    try {
        const perms = usePermissions()
        const currentPath = to.path

        // Trouver les items de menu qui matchent exactement l'URL
        const matchingItems = flatMenu.filter(item => item.to === currentPath)

        // Si la route n'est pas dans le menu, on ne fait rien ici
        if (!matchingItems.length) {
            return
        }

        // Cas particulier : une même route peut apparaître dans plusieurs entrées de menu
        // (ex: Planning étudiant vs Planning personnel). On essaie d'abord de trouver
        // un item pour lequel l'utilisateur a le droit d'accès.
        const allowedItem = matchingItems.find(item => isItemAllowed(item, perms))

        // Si aucun item n'est autorisé, rediriger vers le dashboard
        // if (!allowedItem) {
        //     return navigateTo('/')
        // }

        // L'utilisateur a accès à au moins un des items correspondants, on laisse passer
    } catch (error) {
        // En cas d'erreur (ex: usePermissions échoue), on bloque l'accès par sécurité
        console.error('Erreur dans le middleware ACL:', error)
        return navigateTo('/')
    }
})
